import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Club } from '../models/data.models';

export interface ClubOption {
    id: string;
    name: string;
}

export function clubIdentityKey(name: string | undefined): string {
    if (!name) return '';
    return name
        .trim()
        .replace(/\s+\d+[,.]\d+\s*$/, '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');
}

@Injectable({
    providedIn: 'root'
})
export class ClubService {
    private supabase = inject(SupabaseService);

    private clubNormalizationMap = new Map<string, string>([
        ['a.s.c.c', 'A.S.C.C'],
        ['asla', 'ASLA'],
        ["cani'sports des flandres", "CANI'SPORTS DES FLANDRES"],
        ['canicross club caudry', 'CANICROSS CLUB CAUDRY'],
        ['canisports club de la deûle', 'CANISPORTS CLUB DE LA DEÛLE'],
        ['cross patte', 'LES CROSS PATTES'],
        ['cross pattes', 'LES CROSS PATTES'],
        ['les cross patte', 'LES CROSS PATTES'],
        ['les cross pattes', 'LES CROSS PATTES'],
        ['opale canisports', 'OPALE CANISPORTS'],
        ['happy cani team', 'HAPPY CANI TEAM'],
        ['la meute picarde', 'LA MEUTE PICARDE'],
        ['fbmc', 'FBMC'],
        ['flydogs', 'FLYDOGS'],
        ['canicross 70', 'CANICROSS70'],
    ]);

    /**
     * Every club, read in pages: there are more clubs than the API returns in one response (1000).
     */
    async getClubs(): Promise<Club[]> {
        const clubs: Club[] = [];
        for (let offset = 0; ; offset += 1000) {
            const { data, error } = await this.supabase.client
                .from('clubs')
                .select('id, name, region, department_code, image_url')
                .order('region', { ascending: true })
                .order('department_code', { ascending: true })
                .order('id', { ascending: true })
                .range(offset, offset + 999);
            if (error) throw error;
            clubs.push(...(data || []).map(c => ({
                id: c.id,
                name: c.name,
                region: c.region,
                departmentCode: c.department_code,
                logoUrl: c.image_url
            })));
            if (!data || data.length < 1000) break;
        }
        return clubs;
    }

    /**
     * Clubs whose name contains the query, for pickers. Only id and name are read: the full list
     * exceeds the 1000-row API limit and logos can be large.
     */
    async searchClubs(query: string, limit = 20): Promise<ClubOption[]> {
        const term = query.trim().replace(/[%_]/g, '');
        if (term.length < 2) return [];
        const { data, error } = await this.supabase.client
            .from('clubs')
            .select('id, name')
            .ilike('name', `%${term}%`)
            .order('name')
            .limit(limit);
        if (error) throw error;
        return (data || []) as ClubOption[];
    }

    /** Exact, case-insensitive name lookup used to turn a typed club name into its id. */
    async findClubByName(name: string): Promise<ClubOption | null> {
        const term = name.trim();
        if (!term) return null;
        const { data, error } = await this.supabase.client
            .from('clubs')
            .select('id, name')
            .ilike('name', term.replace(/[%_\\]/g, '\\$&'))
            .limit(2);
        if (error) throw error;
        return data?.length === 1 ? data[0] as ClubOption : null;
    }

    async updateClubLogo(id: string, logoUrl: string): Promise<void> {
        const { error } = await this.supabase.client
            .from('clubs')
            .update({ image_url: logoUrl })
            .eq('id', id)
            .select('id')
            .single();

        if (error) throw error;
    }

    normalizeClubName(name: string | undefined): string | undefined {
        if (!name || name.trim() === '') return undefined;
        const lowerCaseName = name.trim().toLowerCase();
        const lookupName = lowerCaseName
            .replace(/[’']/g, ' ')
            .replace(/\s+\d+[,.]\d+$/, '')
            .replace(/\s+/g, ' ')
            .trim();

        if (this.clubNormalizationMap.has(lookupName)) {
            return this.clubNormalizationMap.get(lookupName);
        }

        // Capitalize first letter of each word
        return lowerCaseName.replace(/(^|\s|-|')\S/g, (char) => char.toUpperCase());
    }
}
